import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Layers, Images, Contrast, Download, ShieldCheck } from "lucide-react";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { useMeta } from "@/useMeta";

const features = [
  "Select one photo or an entire batch",
  "Process up to 50 photos at once",
  "Subscription required",
  "$4.99 monthly or $39.99 annually",
  "No free trial",
  "No sign-in required",
  "On-device processing",
  "No cloud photo storage",
];

const audience = [
  "Online resellers",
  "Product photographers",
  "Clothing sellers",
  "Small businesses",
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 10]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-paper" data-testid="home-hero">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_75%_0%,rgba(31,111,235,0.10),transparent_70%)]"
      />
      <div className="container-x relative grid gap-14 py-20 sm:py-28 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:py-32">
        <div>
          <Reveal>
            <span className="eyebrow">
              <ShieldCheck size={13} /> Private by design
            </span>
          </Reveal>

          <h1 className="mt-6 font-display text-[2.6rem] font-extrabold leading-[0.98] tracking-tight text-ink sm:text-6xl lg:text-[4.6rem]">
            <MaskReveal lines={["Simple", "Background", "Remover"]} delay={0.15} />
          </h1>

          <Reveal delay={0.6}>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-ink-soft sm:text-xl">
              Remove image backgrounds in batches and replace them with pure white or black.
            </p>
          </Reveal>

          <Reveal delay={0.72}>
            <div className="mt-8 flex max-w-xl items-start gap-3 rounded-2xl border border-brand/20 bg-brand-tint/60 p-5">
              <ShieldCheck className="mt-0.5 shrink-0 text-brand" size={20} />
              <p className="text-[15px] font-medium leading-relaxed text-ink" data-testid="home-privacy-statement">
                Your photos stay on your device. Simple Background Remover never uploads, collects, or stores your images.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.82}>
            <a
              href="#features"
              className="group mt-10 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.18em] text-ink transition-colors hover:text-brand"
              data-testid="home-scroll-cue"
            >
              Explore the features
              <ArrowDownRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
          </Reveal>
        </div>

        <motion.div style={{ y, rotate }} className="relative mx-auto w-full max-w-sm lg:mx-0">
          <div aria-hidden="true" className="checker absolute -inset-4 rounded-[2rem] opacity-60" />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="relative rounded-[2rem] border border-line bg-paper p-8 shadow-[0_30px_80px_-40px_rgba(20,50,120,0.4)]"
          >
            <img
              src="/app-icon-512.png"
              alt="Simple Background Remover app icon"
              className="mx-auto w-full max-w-[220px] drop-shadow-xl"
              width="220"
              height="220"
            />
            <div className="mt-6 flex items-center justify-between border-t border-line pt-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faint">On-device</span>
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" /> No uploads
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Features = () => (
  <section id="features" className="relative bg-paper py-24 sm:py-32" data-testid="home-features">
    <div className="container-x">
      <div className="max-w-2xl">
        <Reveal>
          <span className="eyebrow">01 — What it does</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Clean product images, straight from your phone.
          </h2>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2">
        {features.map((f, i) => (
          <Reveal key={f} delay={(i % 2) * 0.06} className="group bg-paper">
            <div className="flex items-start gap-5 p-7 transition-colors duration-300 group-hover:bg-paper-2 sm:p-8">
              <span className="font-mono text-sm text-brand">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="text-lg font-medium leading-snug text-ink">{f}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Audience = () => (
  <section className="relative overflow-hidden bg-paper-2 py-24 sm:py-32" data-testid="home-audience">
    <div className="container-x grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
      <div>
        <Reveal>
          <span className="eyebrow">02 — Who it's for</span>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="mt-6 font-display text-2xl font-bold leading-snug tracking-tight text-ink sm:text-3xl lg:text-[2.4rem]">
            Simple Background Remover is designed for online resellers, product photographers,
            clothing sellers, small businesses, and anyone who needs clean product images.
          </p>
        </Reveal>
      </div>
      <div className="flex flex-wrap gap-3">
        {audience.map((a, i) => (
          <Reveal key={a} delay={i * 0.05}>
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-paper px-5 py-3 text-sm font-semibold text-ink shadow-sm">
              <span className="h-2 w-2 rotate-45 bg-brand" />
              {a}
            </span>
          </Reveal>
        ))}
        <Reveal delay={audience.length * 0.05}>
          <span className="inline-flex items-center rounded-full border border-brand/30 bg-brand-tint px-5 py-3 text-sm font-semibold text-brand-deep">
            …and anyone needing clean product images
          </span>
        </Reveal>
      </div>
    </div>
  </section>
);

const Pricing = () => (
  <section className="relative bg-paper-2 py-24 sm:py-32" data-testid="home-pricing">
    <div className="container-x">
      <div className="max-w-2xl">
        <Reveal>
          <span className="eyebrow">03 - Pricing</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Choose Your Plan
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-ink-soft sm:text-lg">
            Free to download. An active monthly or annual subscription is required.
            No free trial. Subscriptions are purchased and managed through Google Play or the
            Apple App Store.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-2">
        <Reveal>
          <div className="rounded-2xl border border-line bg-paper p-8 shadow-sm">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-faint">Monthly</p>
            <p className="mt-3 text-3xl font-extrabold tracking-tight text-ink">$4.99 per month</p>
          </div>
        </Reveal>

        <Reveal delay={0.06}>
          <div className="rounded-2xl border border-brand/30 bg-brand-tint p-8 shadow-sm">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-brand-deep">Annual</p>
            <p className="mt-3 text-3xl font-extrabold tracking-tight text-ink">$39.99 per year</p>
            <p className="mt-3 inline-flex rounded-full border border-brand/30 bg-paper px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-brand-deep">
              Best Value
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Disclaimer = () => (
  <section className="bg-paper py-20 sm:py-24" data-testid="home-disclaimer">
    <div className="container-x">
      <Reveal>
        <div className="rounded-2xl border border-line bg-paper-2 p-8 sm:p-10">
          <span className="eyebrow">Disclaimer</span>
          <p className="mt-4 max-w-4xl text-base leading-relaxed text-ink-soft">
            Simple Background Remover is an independent application and is not affiliated with or
            endorsed by eBay, Poshmark, Mercari, Etsy, Depop, or any other online marketplace.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  useMeta({
    title: "Simple Background Remover | Private Batch Background Removal",
    description:
      "Simple Background Remover removes image backgrounds in batches on your device and replaces them with pure white or black. Free to download with monthly or annual subscription access. Your photos never leave your phone.",
  });

  return (
    <>
      <Hero />
      <Marquee />
      <Features />
      <Pricing />
      <Audience />
      <Disclaimer />
    </>
  );
}
